import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import CodiceFiscale from "codice-fiscale-js";
import {
  UserPlus, Loader2, Calculator, Calendar, Info, Mail, Send, FileText,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import ComuneAutocomplete from "../ComuneAutocomplete";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  nome: "",
  cognome: "",
  sesso: "",
  codice_fiscale: "",
  data_nascita: "",
  comune_nascita: "",
  comune_nascita_label: "",
  comune_nascita_provincia: "",
  familiare_nome: "",
  familiare_relazione: "",
  familiare_telefono: "",
  familiare_email: "",
  data_ingresso_prevista: "",
  retta_mensile: "",
  deposito_cauzionale_abilitato: false,
  deposito_cauzionale: "",
};

const RELAZIONI = [
  "Figlio/a", "Coniuge", "Nipote", "Fratello/Sorella", "Genitore", "Tutore", "Altro",
];

function defaultInvioMessage(familiare, ospite) {
  return (
    `Gentile ${familiare},\n\n` +
    `in allegato trova il contratto di ingresso per ${ospite}.\n\n` +
    `La preghiamo di completare la procedura guidata online per firmare il contratto e caricare la documentazione richiesta.\n\n` +
    `Cordiali saluti`
  );
}

export default function CreazioneRapidaContrattoDialog({ open, onOpenChange }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [fascicoloId, setFascicoloId] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [invio, setInvio] = useState({
    email: "",
    oggetto: "",
    messaggio: "",
  });
  const [sending, setSending] = useState(false);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const resetAll = () => {
    setForm(EMPTY);
    setStep(1);
    setFascicoloId(null);
    setSelectedDocs([]);
    setInvio({ email: "", oggetto: "", messaggio: "" });
  };

  const handleClose = (v) => {
    if (!v) resetAll();
    onOpenChange(v);
  };

  useEffect(() => {
    if (!open) return;
    axios.get(`${API_URL}/api/preingresso/catalog`).then(({ data }) => {
      setCatalog(data.documents || []);
      setSelectedDocs(data.defaults || []);
    }).catch(() => {});
  }, [open]);

  const handleCalcolaCodiceFiscale = () => {
    const nome = form.nome.trim();
    const cognome = form.cognome.trim();
    const sesso = form.sesso;
    const dataNascita = form.data_nascita;
    if (!nome || !cognome || !sesso || !dataNascita) {
      toast.error("Compila nome, cognome, sesso e data di nascita.");
      return;
    }
    const parts = String(dataNascita).split("-");
    if (parts.length !== 3) {
      toast.error("Data di nascita non valida.");
      return;
    }
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    const comuneLabel = form.comune_nascita_label.trim();
    const prov = form.comune_nascita_provincia.trim();
    if (!comuneLabel || !prov) {
      toast.error("Seleziona il comune di nascita dall'elenco.");
      return;
    }
    try {
      const cf = CodiceFiscale.compute({
        name: nome,
        surname: cognome,
        gender: sesso === "F" ? "F" : "M",
        day, month, year,
        birthplace: comuneLabel,
        birthplaceProvincia: prov,
      });
      set("codice_fiscale", cf);
      toast.success("Codice fiscale calcolato");
    } catch (e) {
      toast.error(e?.message || "Impossibile calcolare il codice fiscale.");
    }
  };

  const validateForm = () => {
    if (!form.nome.trim() || !form.cognome.trim()) {
      toast.error("Nome e cognome sono obbligatori");
      return false;
    }
    if (!form.sesso || !form.data_nascita || !form.comune_nascita_label.trim()) {
      toast.error("Completa i dati anagrafici dell'ospite");
      return false;
    }
    if (!form.codice_fiscale.trim()) {
      toast.error("Inserisci o calcola il codice fiscale");
      return false;
    }
    if (!form.familiare_nome.trim() || !form.familiare_relazione || !form.familiare_telefono.trim()) {
      toast.error("Completa i dati del familiare/tutore");
      return false;
    }
    if (!form.data_ingresso_prevista || !form.retta_mensile || parseFloat(form.retta_mensile) <= 0) {
      toast.error("Data ingresso e retta mensile obbligatori");
      return false;
    }
    if (form.deposito_cauzionale_abilitato) {
      const dep = parseFloat(form.deposito_cauzionale);
      if (!form.deposito_cauzionale || !Number.isFinite(dep) || dep <= 0) {
        toast.error("Inserisci l'importo del deposito cauzionale");
        return false;
      }
    }
    return true;
  };

  const createFascicolo = async () => {
    const createRes = await axios.post(
      `${API_URL}/api/fascicoli-assistenziali`,
      {
        nome: form.nome.trim(),
        cognome: form.cognome.trim(),
        sesso: form.sesso,
        codiceFiscale: form.codice_fiscale.trim().toUpperCase(),
        dataNascita: form.data_nascita,
        comuneNascita: form.comune_nascita_label.trim(),
        dataIngressoPrevista: form.data_ingresso_prevista,
        retta_mensile: parseFloat(form.retta_mensile),
        deposito_cauzionale_abilitato: !!form.deposito_cauzionale_abilitato,
        deposito_cauzionale: form.deposito_cauzionale_abilitato
          ? parseFloat(form.deposito_cauzionale)
          : null,
        tipo_ospite: "residente",
        createdFrom: "dashboard_contratto_rapido",
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const fid = createRes.data.cartella_id || createRes.data.fascicolo_id;
    if (!fid) throw new Error("ID fascicolo non ricevuto");

    await axios.put(
      `${API_URL}/api/fascicoli-assistenziali/${fid}`,
      {
        sezione: "anagrafica",
        dati: {
          comune_nascita: form.comune_nascita,
          comune_nascita_label: form.comune_nascita_label.trim(),
          comune_nascita_provincia: form.comune_nascita_provincia.trim(),
          luogo_nascita: form.comune_nascita_label.trim(),
          stato_nascita_label: "ITALIA",
          contatti_familiari: [{
            nome: form.familiare_nome.trim(),
            relazione: form.familiare_relazione,
            telefono: form.familiare_telefono.trim(),
            email: form.familiare_email.trim(),
          }],
        },
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return fid;
  };

  const handleSaveAndSign = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const fid = await createFascicolo();
      toast.success("Ospite creato — procedi con la firma del contratto");
      handleClose(false);
      navigate(`/fascicoli-assistenziali/${fid}/firma-contratto`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndSend = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const fid = await createFascicolo();
      setFascicoloId(fid);
      const ospite = `${form.nome.trim()} ${form.cognome.trim()}`;
      setInvio({
        email: form.familiare_email.trim(),
        oggetto: `Contratto di ingresso e documenti richiesti - ${ospite}`,
        messaggio: defaultInvioMessage(form.familiare_nome.trim(), ospite),
      });
      setStep(2);
      toast.success("Ospite creato in pre-ingresso");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione");
    } finally {
      setSaving(false);
    }
  };

  const toggleDoc = (key) => {
    setSelectedDocs((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleInviaRichiesta = async () => {
    if (!invio.email.trim()) {
      toast.error("Email destinatario obbligatoria");
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/pre-ingresso/invia`,
        {
          email: invio.email.trim(),
          oggetto: invio.oggetto.trim(),
          messaggio: invio.messaggio.trim(),
          requested_documents: selectedDocs,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Richiesta inviata al familiare");
      handleClose(false);
      navigate(`/fascicoli-assistenziali/${fascicoloId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio");
    } finally {
      setSending(false);
    }
  };

  const ospiteNome = `${form.nome.trim()} ${form.cognome.trim()}`.trim();

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        {step === 1 ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-emerald-600" />
                Creazione rapida con firma contratto
              </DialogTitle>
              <DialogDescription>
                Inserisci i dati necessari. Puoi firmare in struttura o inviare il link al familiare.
              </DialogDescription>
            </DialogHeader>

            <div className="py-2 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-0">
                <div className="space-y-4 md:pr-6">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Dati ospite</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Nome *</Label>
                      <Input value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Mario" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Cognome *</Label>
                      <Input value={form.cognome} onChange={(e) => set("cognome", e.target.value)} placeholder="Rossi" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Sesso *</Label>
                      <Select value={form.sesso || undefined} onValueChange={(v) => set("sesso", v)}>
                        <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="M">Maschio</SelectItem>
                          <SelectItem value="F">Femmina</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Data di nascita *</Label>
                      <Input type="date" value={form.data_nascita} onChange={(e) => set("data_nascita", e.target.value)} />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Comune di nascita *</Label>
                    <ComuneAutocomplete
                      value={form.comune_nascita_label}
                      onChange={(val) => {
                        setForm((p) => ({
                          ...p,
                          comune_nascita_label: val,
                          ...(val ? {} : { comune_nascita: "", comune_nascita_provincia: "" }),
                        }));
                      }}
                      onSelect={(comune) => {
                        setForm((p) => ({
                          ...p,
                          comune_nascita: comune.codice,
                          comune_nascita_label: comune.descrizione,
                          comune_nascita_provincia: comune.provincia || "",
                        }));
                      }}
                      placeholder="Cerca comune..."
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Codice fiscale *</Label>
                    <div className="flex gap-2">
                      <Input
                        value={form.codice_fiscale}
                        onChange={(e) => set("codice_fiscale", e.target.value.toUpperCase())}
                        maxLength={16}
                        className="font-mono flex-1"
                      />
                      <Button type="button" variant="outline" onClick={handleCalcolaCodiceFiscale}>
                        <Calculator className="w-4 h-4 mr-1" /> Calcola CF
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 md:pl-6 md:border-l border-slate-200 pt-4 md:pt-0">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Familiare / Tutore</p>
                  <div className="space-y-1.5">
                    <Label>Nome completo *</Label>
                    <Input value={form.familiare_nome} onChange={(e) => set("familiare_nome", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Relazione *</Label>
                    <Select value={form.familiare_relazione || undefined} onValueChange={(v) => set("familiare_relazione", v)}>
                      <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                      <SelectContent>
                        {RELAZIONI.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Telefono *</Label>
                    <Input type="tel" value={form.familiare_telefono} onChange={(e) => set("familiare_telefono", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input type="email" value={form.familiare_email} onChange={(e) => set("familiare_email", e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 space-y-2">
                  <Label>Data ingresso prevista *</Label>
                  <Input type="date" value={form.data_ingresso_prevista} onChange={(e) => set("data_ingresso_prevista", e.target.value)} />
                  <p className="text-xs text-emerald-800 flex gap-1"><Info className="w-3.5 h-3.5 shrink-0" /> L&apos;ospite sarà creato come pre-ingresso.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Retta mensile (€) *</Label>
                  <Input type="number" min="0" step="0.01" value={form.retta_mensile} onChange={(e) => set("retta_mensile", e.target.value)} />
                </div>
                <div className="space-y-2 rounded-lg border border-slate-200 p-3 md:col-span-2">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label htmlFor="dep-cauz-flag">Deposito cauzionale</Label>
                      <p className="text-xs text-slate-500 mt-0.5">Opzionale. Se abilitato, l&apos;importo è obbligatorio.</p>
                    </div>
                    <Switch
                      id="dep-cauz-flag"
                      checked={!!form.deposito_cauzionale_abilitato}
                      onCheckedChange={(checked) => {
                        const on = checked === true;
                        setForm((p) => ({
                          ...p,
                          deposito_cauzionale_abilitato: on,
                          deposito_cauzionale: on ? p.deposito_cauzionale : "",
                        }));
                      }}
                    />
                  </div>
                  {form.deposito_cauzionale_abilitato && (
                    <div className="space-y-1.5">
                      <Label>Importo (€) *</Label>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={form.deposito_cauzionale}
                        onChange={(e) => set("deposito_cauzionale", e.target.value)}
                        placeholder="0,00"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => handleClose(false)} disabled={saving}>Annulla</Button>
              <Button variant="outline" className="border-emerald-300 text-emerald-700" onClick={handleSaveAndSign} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Salva e vai alla firma
              </Button>
              <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleSaveAndSend} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                Salva, invia contratto e richiedi documenti
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-emerald-600" />
                Invia contratto e richiedi documenti
              </DialogTitle>
              <DialogDescription>
                Personalizza l&apos;email e seleziona i documenti da richiedere a {ospiteNome}.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 py-2">
              <div className="lg:col-span-2 space-y-4">
                <div className="space-y-1.5">
                  <Label>Destinatario *</Label>
                  <Input value={invio.email} onChange={(e) => setInvio((p) => ({ ...p, email: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label>Oggetto</Label>
                  <Input value={invio.oggetto} onChange={(e) => setInvio((p) => ({ ...p, oggetto: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center">
                    <Label>Messaggio</Label>
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-xs"
                      onClick={() => setInvio((p) => ({
                        ...p,
                        messaggio: defaultInvioMessage(form.familiare_nome.trim(), ospiteNome),
                      }))}
                    >
                      Ripristina testo predefinito
                    </Button>
                  </div>
                  <Textarea rows={8} value={invio.messaggio} onChange={(e) => setInvio((p) => ({ ...p, messaggio: e.target.value }))} />
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-slate-50">
                  <FileText className="w-8 h-8 text-red-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">Contratto di ingresso - {ospiteNome}.pdf</p>
                    <span className="text-xs text-emerald-600 font-medium">Pronto</span>
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Documenti da richiedere (opzionale)</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Puoi deselezionare tutti i documenti se vuoi inviare solo il contratto da firmare.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 border rounded-lg p-3">
                    {catalog.map((doc) => (
                      <label key={doc.key} className="flex items-start gap-2 text-sm cursor-pointer py-1">
                        <Checkbox
                          checked={selectedDocs.includes(doc.key)}
                          onCheckedChange={() => toggleDoc(doc.key)}
                          className="mt-0.5"
                        />
                        <span className="leading-snug">{doc.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm">
                <p className="font-semibold text-emerald-900 mb-3">Cosa succede dopo l&apos;invio?</p>
                <ol className="space-y-2 text-emerald-800 list-decimal list-inside text-xs">
                  <li>Il contratto verrà inviato al destinatario</li>
                  <li>L&apos;ospite sarà creato con stato pre-ingresso</li>
                  <li>Potrai monitorare lo stato nel fascicolo assistenziale</li>
                </ol>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)} disabled={sending}>Annulla</Button>
              <Button variant="outline" onClick={() => setStep(1)} disabled={sending}>Indietro</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleInviaRichiesta} disabled={sending}>
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Invia richiesta
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
